import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

type SidebarSelection = "share-price-performance";

export const SLIDE_FRAME_KEYS = [
  "slideFrame1",
  "slideFrame2",
  "slideFrame3",
  "slideFrame4",
  "slideFrame5",
  "slideFrame6",
  "slideFrame7",
  "slideFrame8",
  "slideFrame9",
] as const;

export type SlideFrameKey = (typeof SLIDE_FRAME_KEYS)[number];

/** Exactly one precedent frame key, or null if none / using Create New path. */
export type SelectedSlideFrameKey = SlideFrameKey | null;

/** Primary company chosen in “Select Precedent” (aligned with console peer company fields). */
export type ContentSlidePrimaryCompany = {
  companyName: string;
  companyDomain: string;
  companyTicker: string;
  companyExchange: string;
  symbol: string;
  companyColor: string;
  companyIpoDate: string | null;
};

export type ImpliedColorRow = {
  label: string;
  rgb: string;
};

interface ContentSlidePopupState {
  sidebar: SidebarSelection;
  setSidebar: (value: SidebarSelection) => void;
  showSidebar: boolean;
  setShowSidebar: (value: boolean) => void;
  primaryCompany: ContentSlidePrimaryCompany | null;
  setPrimaryCompany: (value: ContentSlidePrimaryCompany | null) => void;
  selectedSlideFrameKey: SelectedSlideFrameKey;
  /** Pass a frame key to select it (clears Create New). Same key again toggles off; pass null to clear only. */
  setSelectedSlideFrameKey: (key: SlideFrameKey | null) => void;
  precedentPanelMode: "grid" | "createNew";
  setPrecedentPanelMode: (mode: "grid" | "createNew") => void;
  createNewPrecedentPending: boolean;
  setCreateNewPrecedentPending: (value: boolean) => void;
  /** User chose “Create New” on the grid; Next opens {@link openCreateNewPrecedentPanel} when no frame is selected. */
  createNewPrecedentIntent: boolean;
  setCreateNewPrecedentIntent: (value: boolean) => void;
  openCreateNewPrecedentPanel: () => void;
  wizardStep: number;
  setWizardStep: (step: number) => void;
  wizardAnswersByStep: [number, number, number];
  setWizardAnswersByStep: (answers: [number, number, number]) => void;
  selectedFormatTemplateId: string | null;
  setSelectedFormatTemplateId: (id: string | null) => void;
  impliedColorRows: ImpliedColorRow[];
  setImpliedColorRows: (rows: ImpliedColorRow[]) => void;
  updateImpliedColorRgb: (index: number, rgb: string) => void;
}

const oneShotStorage: Storage = {
  getItem: (key) => {
    const v = sessionStorage.getItem(key);
    if (v !== null) sessionStorage.removeItem(key);
    return v;
  },
  setItem: (key, value) => sessionStorage.setItem(key, value),
  removeItem: (key) => sessionStorage.removeItem(key),
} as Storage;

export const useContentSlideStore = create<ContentSlidePopupState>()(
  persist(
    (set) => ({
      sidebar: "share-price-performance",
      setSidebar: (value) => set({ sidebar: value }),
      showSidebar: true,
      setShowSidebar: (value) => set({ showSidebar: value }),
      primaryCompany: null,
      setPrimaryCompany: (value) => set({ primaryCompany: value }),
      selectedSlideFrameKey: null,
      setSelectedSlideFrameKey: (key) =>
        set((s) => {
          if (key === null) {
            return {
              selectedSlideFrameKey: null,
            };
          }
          const nextKey = s.selectedSlideFrameKey === key ? null : key;
          return {
            selectedSlideFrameKey: nextKey,
            createNewPrecedentIntent: nextKey ? false : s.createNewPrecedentIntent,
            createNewPrecedentPending: nextKey ? false : s.createNewPrecedentPending,
          };
        }),
      precedentPanelMode: "grid",
      setPrecedentPanelMode: (mode) => set({ precedentPanelMode: mode }),
      createNewPrecedentPending: false,
      setCreateNewPrecedentPending: (value) => set({ createNewPrecedentPending: value }),
      createNewPrecedentIntent: false,
      setCreateNewPrecedentIntent: (value) =>
        set((s) => ({
          createNewPrecedentIntent: value,
          ...(value ? { selectedSlideFrameKey: null as SelectedSlideFrameKey } : {}),
        })),
      openCreateNewPrecedentPanel: () =>
        set({
          precedentPanelMode: "createNew",
          createNewPrecedentPending: false,
          createNewPrecedentIntent: false,
        }),
      wizardStep: 0,
      setWizardStep: (step) => set({ wizardStep: step }),
      wizardAnswersByStep: [0, 0, 0],
      setWizardAnswersByStep: (wizardAnswersByStep) => set({ wizardAnswersByStep }),
      selectedFormatTemplateId: null,
      setSelectedFormatTemplateId: (id) => set({ selectedFormatTemplateId: id }),
      impliedColorRows: [],
      setImpliedColorRows: (impliedColorRows) => set({ impliedColorRows }),
      updateImpliedColorRgb: (index, rgb) =>
        set((s) => {
          if (index < 0 || index >= s.impliedColorRows.length) return s;
          const impliedColorRows = s.impliedColorRows.map((row, i) =>
            i === index ? { ...row, rgb } : row,
          );
          return { impliedColorRows };
        }),
    }),
    {
      name: "contentSlidePopup",
      storage: createJSONStorage(() => oneShotStorage),
      partialize: (s) => ({
        showSidebar: s.showSidebar,
      }),
      version: 5,
    }
  )
);
